/*********************************************************************************
 * Copyright (C) 2025 VECROS Technologies Pvt. Ltd. <legal@vecros.com>
 * Proprietary and confidential.
 **********************************************************************************/

import React, { useState } from "react";
import { Box, Typography, InputBase, useTheme } from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ArrowUpwardRoundedIcon from "@mui/icons-material/ArrowUpwardRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import DevicesOutlinedIcon from "@mui/icons-material/DevicesOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import SpeedOutlinedIcon from "@mui/icons-material/SpeedOutlined";
import { useUserContext } from "../context/UserContext";
import { useAssistant } from "./assistant";
import { AI_ACCENT, AI_GRADIENT } from "./assistant/model/tokens";
import { ROUTES } from "../routes";
import { Link as RouterLink } from "react-router-dom";

const greeting = () => {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
};

const SUGGESTIONS = [
  "How do I register a new device?",
  "Summarise the health of my active devices",
  "Show me how to create an API key",
];

// Quick actions navigate to the matching developer page.
const QUICK_ACTIONS = [
  { label: "Register a device", desc: "Add hardware to your fleet", Icon: DevicesOutlinedIcon, path: ROUTES.devices },
  { label: "Read the docs", desc: "Guides, references & tutorials", Icon: MenuBookOutlinedIcon, path: ROUTES.docs },
  { label: "Downloads", desc: "SDKs, firmware & tools", Icon: DownloadOutlinedIcon, path: ROUTES.downloads },
  { label: "Thrust Stand", desc: "Motor & ESC bench tooling", Icon: SpeedOutlinedIcon, path: ROUTES.thrustStand },
];

// Ask-assistant entry cards (open the assistant with a seeded prompt).
const CARDS = [
  {
    tag: "Devices",
    title: "Check on your fleet",
    body: "Surface devices that need attention, firmware updates, or registration issues.",
    seed: "Which of my devices need attention right now?",
  },
  {
    tag: "Integrations",
    title: "Wire up the API",
    body: "Get help creating keys, authenticating requests, and calling the developer API.",
    seed: "Walk me through creating an API key and making my first authenticated request.",
  },
  {
    tag: "Platform",
    title: "Explore AI services",
    body: "Understand what the AI Agents and AI Models services can do for your build.",
    seed: "Explain the AI Agents and AI Models services and when to use each.",
  },
];

const FAQS = [
  {
    q: "How do I register a new device?",
    a: "Open the Devices page and generate an install command. Run it on your device — the agent installs and registers it automatically, then it appears in your fleet.",
  },
  {
    q: "Where do I get my API keys?",
    a: "Go to API Keys under Accounts in the sidebar. Create a key, copy it once (it is shown only at creation), and pass it as a Bearer token in the Authorization header of your requests.",
  },
  {
    q: "What can the AI Assistant do?",
    a: "Ask it anything about your account, devices, or the platform. It can answer questions, walk you through tasks, and navigate you to the right page. Type @ to mention one of your devices for grounded answers.",
  },
  {
    q: "Where can I download SDKs and firmware?",
    a: "The Downloads page lists SDKs, firmware images, and tools. Some artifacts are access-controlled and open through Protected Downloads.",
  },
  {
    q: "How do I upgrade my plan?",
    a: "Open your profile menu in the top-right and choose Upgrade Plan, or head to Plans & Billing to review usage and manage your subscription.",
  },
];

function FaqItem({ q, a }) {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  return (
    <Box
      sx={{
        border: `1px solid ${theme.palette.border.color}`,
        borderRadius: "12px",
        backgroundColor: theme.palette.secondary.main,
        overflow: "hidden",
      }}
    >
      <Box
        role="button"
        tabIndex={0}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && setOpen((o) => !o)}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 2,
          py: 1.5,
          cursor: "pointer",
          "&:hover": { backgroundColor: theme.palette.tertiary.main },
        }}
      >
        <Typography sx={{ flex: 1, fontSize: 14.5, fontWeight: 600, color: theme.palette.text.color }}>
          {q}
        </Typography>
        <ExpandMoreRoundedIcon
          sx={{
            fontSize: 22,
            color: theme.palette.secondaryText?.color,
            transform: open ? "rotate(180deg)" : "none",
            transition: "transform 0.2s ease",
          }}
        />
      </Box>
      {open && (
        <Typography
          sx={{
            px: 2,
            pb: 1.75,
            fontSize: 13.5,
            lineHeight: 1.6,
            color: theme.palette.secondaryText?.color,
          }}
        >
          {a}
        </Typography>
      )}
    </Box>
  );
}

/**
 * Developer Overview / home — an AI launchpad in the accounts style, extended with a
 * quick-actions grid and an FAQ. The ask box and cards open the persistent assistant
 * panel (which shrinks this page rather than overlapping it — see Body.jsx).
 */
export default function Overview() {
  const theme = useTheme();
  const { userDetails } = useUserContext();
  const { openPanel, send } = useAssistant();
  const [text, setText] = useState("");

  const name = userDetails?.user_first_name;

  const ask = (prompt) => {
    const t = (prompt || "").trim();
    if (!t) return;
    openPanel();
    send(t);
    setText("");
  };

  const sectionTitle = {
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: theme.palette.secondaryText?.color,
    mb: 2,
  };

  return (
    <Box
      sx={{
        boxSizing: "border-box",
        backgroundColor: theme.palette.primary.main,
        minHeight: "100%",
        p: { xs: 2, md: 4 },
        color: theme.palette.text.color,
        overflow: "auto",
        scrollbarWidth: "none",
      }}
    >
      <Box sx={{ maxWidth: 860, mx: "auto", pt: { xs: 2, md: 5 } }}>
        {/* Hero */}
        <Box sx={{ textAlign: "center" }}>
          <Box
            aria-hidden
            sx={{
              width: 56,
              height: 56,
              borderRadius: "16px",
              display: "inline-grid",
              placeItems: "center",
              background: AI_GRADIENT,
              color: "#fff",
              mb: 2,
            }}
          >
            <AutoAwesomeIcon sx={{ fontSize: 28 }} />
          </Box>
          <Typography sx={{ color: theme.palette.secondaryText?.color, fontSize: 16 }}>
            {greeting()}{name ? `, ${name}` : ""}
          </Typography>
          <Typography sx={{ fontSize: 28, fontWeight: 600, letterSpacing: "-0.02em" }}>
            How can I help?
          </Typography>

          {/* Ask anything */}
          <Box
            sx={{
              mt: 3,
              display: "flex",
              alignItems: "center",
              gap: 1,
              maxWidth: 620,
              mx: "auto",
              px: 1.5,
              py: 1,
              border: `1px solid ${theme.palette.border.color}`,
              borderRadius: "14px",
              backgroundColor: theme.palette.secondary.main,
              transition: "border-color 0.15s ease",
              "&:focus-within": { borderColor: AI_ACCENT },
            }}
          >
            <InputBase
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") ask(text);
              }}
              placeholder="Ask anything about your devices, API, or the platform…"
              sx={{ flex: 1, fontSize: 15, color: theme.palette.text.color }}
              inputProps={{ "aria-label": "Ask the assistant" }}
            />
            <Box
              role="button"
              aria-label="Ask"
              onClick={() => ask(text)}
              sx={{
                display: "grid",
                placeItems: "center",
                width: 34,
                height: 34,
                borderRadius: "10px",
                cursor: text.trim() ? "pointer" : "default",
                backgroundColor: text.trim() ? AI_ACCENT : theme.palette.border.color,
                color: "#fff",
              }}
            >
              <ArrowUpwardRoundedIcon sx={{ fontSize: 20 }} />
            </Box>
          </Box>

          {/* Suggestion chips */}
          <Box sx={{ mt: 2, display: "flex", flexWrap: "wrap", gap: 1, justifyContent: "center" }}>
            {SUGGESTIONS.map((s) => (
              <Box
                key={s}
                role="button"
                tabIndex={0}
                onClick={() => ask(s)}
                onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && ask(s)}
                sx={{
                  fontSize: 13,
                  px: 1.5,
                  py: 0.75,
                  borderRadius: "999px",
                  cursor: "pointer",
                  border: `1px solid ${theme.palette.border.color}`,
                  color: theme.palette.text.color,
                  "&:hover": { borderColor: AI_ACCENT },
                }}
              >
                {s}
              </Box>
            ))}
          </Box>
        </Box>

        {/* Quick actions */}
        <Box sx={{ mt: 6 }}>
          <Typography sx={sectionTitle}>Quick actions</Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr" },
              gap: 2,
            }}
          >
            {QUICK_ACTIONS.map((a) => (
              <Box
                key={a.label}
                component={RouterLink}
                to={a.path}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  p: 2,
                  cursor: "pointer",
                  textDecoration: "none",
                  color: "inherit",
                  borderRadius: "12px",
                  border: `1px solid ${theme.palette.border.color}`,
                  backgroundColor: theme.palette.secondary.main,
                  transition: "transform 0.15s ease, border-color 0.15s ease",
                  "&:hover": { transform: "translateY(-2px)", borderColor: AI_ACCENT },
                }}
              >
                <Box
                  aria-hidden
                  sx={{
                    width: 40,
                    height: 40,
                    flexShrink: 0,
                    borderRadius: "10px",
                    display: "grid",
                    placeItems: "center",
                    backgroundColor: "rgba(0,107,206,0.10)",
                    color: AI_ACCENT,
                  }}
                >
                  <a.Icon sx={{ fontSize: 22 }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: 14.5, fontWeight: 600, color: theme.palette.text.color }}>
                    {a.label}
                  </Typography>
                  <Typography sx={{ fontSize: 12.5, color: theme.palette.secondaryText?.color }}>
                    {a.desc}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Explore with the assistant */}
        <Box sx={{ mt: 6 }}>
          <Typography sx={sectionTitle}>Explore with the assistant</Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" },
              gap: 2,
            }}
          >
            {CARDS.map((c) => (
              <Box
                key={c.title}
                role="button"
                tabIndex={0}
                onClick={() => ask(c.seed)}
                onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && ask(c.seed)}
                sx={{
                  p: 2,
                  cursor: "pointer",
                  display: "flex",
                  flexDirection: "column",
                  borderRadius: "14px",
                  border: `1px solid ${theme.palette.border.color}`,
                  backgroundColor: theme.palette.secondary.main,
                  transition: "transform 0.15s ease, border-color 0.15s ease",
                  "&:hover": { transform: "translateY(-2px)", borderColor: AI_ACCENT },
                }}
              >
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 600,
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                    color: AI_ACCENT,
                    mb: 1,
                  }}
                >
                  {c.tag}
                </Typography>
                <Typography sx={{ fontSize: 16, fontWeight: 600, mb: 0.5 }}>{c.title}</Typography>
                <Typography
                  sx={{
                    fontSize: 13,
                    color: theme.palette.secondaryText?.color,
                    lineHeight: 1.5,
                    flex: 1,
                  }}
                >
                  {c.body}
                </Typography>
                <Box
                  sx={{
                    mt: 1.5,
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    fontSize: 13,
                    fontWeight: 600,
                    color: AI_ACCENT,
                  }}
                >
                  Ask the assistant
                  <ArrowForwardRoundedIcon sx={{ fontSize: 16 }} />
                </Box>
              </Box>
            ))}
          </Box>
        </Box>

        {/* FAQ */}
        <Box sx={{ mt: 6, pb: 4 }}>
          <Typography sx={sectionTitle}>Frequently asked</Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
            {FAQS.map((f) => (
              <FaqItem key={f.q} q={f.q} a={f.a} />
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
